/**
 * Add Book Flow Component
 * 
 * Parent-facing interface for adding books to their library.
 * 
 * Flow:
 * 1. Parent scans ISBN barcode OR enters title manually
 * 2. Check if book exists in global library
 * 3. If yes: Add to parent's library instantly
 * 4. If no: Create scan request, show 24h processing message
 * 5. If parent has multiple children: Ask which child(ren) this book is for
 *    (single-child families auto-assign silently)
 * 
 * Uses IsbnScanner component for visual/functional parity with admin scanner.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  X,
  Camera,
  Keyboard,
  Loader2,
  CheckCircle,
  Clock,
  Search,
  AlertCircle,
  Check,
  BookOpen,
  Lightbulb,
  ArrowLeft,
  Send,
} from 'lucide-react';
import { glassStyles } from '@/lib/design';
import { useAuth } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { useViewAs } from '@/contexts/ViewAsContext';
import { useSelectedChild } from '@/contexts/ChildContext';
import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
import { IsbnScanner } from '@/components/scanner';
import {
  addBookToLibrary,
  lookupIsbn,
  createBookScanRequest,
  checkForDuplicate,
} from '@/services/scan';

// ============================================================================
// Types
// ============================================================================

type FlowStep = 
  | 'choose-method'
  | 'choose-pages-method'
  | 'isbn-scan'
  | 'manual-entry'
  | 'checking'
  | 'found-adding'
  | 'not-found-requesting'
  | 'choose-children'
  | 'success'
  | 'request-submitted'
  | 'knowledge-input'
  | 'knowledge-confirming'
  | 'knowledge-confirmed'
  | 'knowledge-submitted';

interface BookInfo {
  isbn?: string;
  title: string;
  author?: string;
  coverImageUrl?: string;
  publisher?: string;
  binding?: string;
}

interface ChildOption {
  id: string;
  name: string;
  selected: boolean;
}

interface AddBookFlowProps {
  onClose: () => void;
  onBookAdded?: (bookId: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export function AddBookFlow({ onClose, onBookAdded }: AddBookFlowProps) {
  const { profile } = useAuth();
  const { t } = useTranslation();
  const { effectiveParentId } = useViewAs();
  const { children: contextChildren, selectedChildId, isLoading: childContextLoading } = useSelectedChild();

  // Use effectiveParentId for data queries (supports admin preview via ?viewAs=)
  const parentId = effectiveParentId || profile?.id;
  const [step, setStep] = useState<FlowStep>('choose-method');
  const [bookInfo, setBookInfo] = useState<BookInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Manual ISBN entry
  const [manualIsbn, setManualIsbn] = useState('');

  // Turn Knowledge state
  const [knowledgeTopic, setKnowledgeTopic] = useState('');
  const [knowledgeConfirmation, setKnowledgeConfirmation] = useState('');
  const [knowledgeQuestionType, setKnowledgeQuestionType] = useState('What');
  const [knowledgeTopicClean, setKnowledgeTopicClean] = useState('');
  const [knowledgeError, setKnowledgeError] = useState<string | null>(null);

  // Child assignment — initialized from ChildContext
  const [childOptions, setChildOptions] = useState<ChildOption[]>([]);
  const [addedBookId, setAddedBookId] = useState<string | null>(null);
  const childrenLoaded = !childContextLoading;

  // Refs track latest values so handleBookAdded never reads stale closures
  const childOptionsRef = useRef<ChildOption[]>([]);
  childOptionsRef.current = childOptions;
  const childrenLoadedRef = useRef(false);
  childrenLoadedRef.current = childrenLoaded;

  // Sync children from context into childOptions (all selected by default)
  useEffect(() => {
    if (!childContextLoading && contextChildren.length > 0) {
      const options = contextChildren.map(c => ({ id: c.id, name: c.name, selected: true }));
      setChildOptions(options);
      childOptionsRef.current = options;
    }
  }, [contextChildren, childContextLoading]);

  // ============================================================================
  // Navigation helpers
  // ============================================================================

  const startIsbnScanner = useCallback(() => {
    setStep('isbn-scan');
    setError(null);
  }, []);

  // ============================================================================
  // Child Assignment Helper
  // ============================================================================

  /**
   * After a book is added to the parent's library, assign it to children.
   * - Single child: auto-assign, go straight to success
   * - Multiple children: show picker step
   * 
   * IMPORTANT: Reads from childOptionsRef (not closure) to avoid race condition
   * where children haven't loaded yet when user scans an ISBN quickly.
   */
  const handleBookAdded = useCallback(async (bookId: string) => {
    setAddedBookId(bookId);

    // Wait for ChildContext to finish loading if still in-flight (fast ISBN scan race)
    if (!childrenLoadedRef.current) {
      await new Promise<void>((resolve) => {
        const check = setInterval(() => {
          if (childrenLoadedRef.current) {
            clearInterval(check);
            resolve();
          }
        }, 50);
        // Safety timeout (network failure, etc.)
        setTimeout(() => { clearInterval(check); resolve(); }, 5000);
      });
    }

    const kids = childOptionsRef.current;

    if (kids.length <= 1) {
      // Single child or no children: auto-assign using selectedChildId
      if (kids.length === 1 && profile?.id) {
        await supabase.from('child_book_library').upsert(
          { child_id: kids[0].id, book_id: bookId, assigned_by: profile.id },
          { onConflict: 'child_id,book_id' }
        );
      }
      setStep('success');
      onBookAdded?.(bookId);
    } else {
      // Multiple children: let parent pick
      setStep('choose-children');
    }
  }, [childrenLoaded, profile?.id, onBookAdded]);

  const confirmChildAssignment = useCallback(async () => {
    if (!addedBookId || !profile?.id) return;
    setIsProcessing(true);

    try {
      const selectedChildren = childOptions.filter(c => c.selected);
      for (const child of selectedChildren) {
        await supabase.from('child_book_library').upsert(
          { child_id: child.id, book_id: addedBookId, assigned_by: profile.id },
          { onConflict: 'child_id,book_id' }
        );
      }
      setStep('success');
      onBookAdded?.(addedBookId);
    } catch (err) {
      console.error('[AddBookFlow] Error assigning to children:', err);
      Sentry.captureException(err, { tags: { flow: 'add_book', step: 'child_assignment' }, extra: { bookId: addedBookId } });
      setStep('success');
      onBookAdded?.(addedBookId);
    } finally {
      setIsProcessing(false);
    }
  }, [addedBookId, childOptions, profile?.id, onBookAdded]);

  // ============================================================================
  // ISBN Processing
  // ============================================================================

  const handleIsbnDetected = useCallback(async (rawIsbn: string) => {
    const isbn = rawIsbn.replace(/[^0-9Xx]/g, '').toUpperCase();
    setStep('checking');
    setIsProcessing(true);
    setError(null);

    try {
      const metadata = await lookupIsbn(isbn);
      
      const info: BookInfo = {
        isbn,
        title: metadata?.title || 'Unknown Title',
        author: metadata?.author || undefined,
        coverImageUrl: metadata?.coverImageUrl || undefined,
        publisher: metadata?.publisher || undefined,
        binding: metadata?.binding || undefined,
      };
      setBookInfo(info);

      if (!profile?.id) return;

      const dup = await checkForDuplicate(isbn);

      if (dup.exists) {
        setStep('found-adding');
        await addBookToLibrary(profile.id, dup.bookId);
        await handleBookAdded(dup.bookId);
      } else {
        setStep('not-found-requesting');
        await createBookScanRequest(profile.id, info.title, {
          isbn: info.isbn,
          author: info.author,
          coverImageUrl: info.coverImageUrl,
          publisher: info.publisher,
          binding: info.binding,
        });
        setStep('request-submitted');
      }
    } catch (err) {
      console.error('[AddBookFlow] Error processing ISBN:', err);
      Sentry.captureException(err, { tags: { flow: 'add_book', step: 'isbn_detected' }, extra: { isbn } });
      setError(t('Something went wrong. Please try again.'));
      setStep('choose-method');
    } finally {
      setIsProcessing(false);
    }
  }, [profile?.id, handleBookAdded, t]);

  // ============================================================================
  // Manual Entry
  // ============================================================================

  const handleManualSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!manualIsbn.trim()) {
      setError(t('Please enter an ISBN number'));
      return;
    }

    setStep('checking');
    setIsProcessing(true);
    setError(null);

    try {
      const isbn = manualIsbn.trim().replace(/[^0-9Xx]/g, '').toUpperCase();
      const metadata = await lookupIsbn(isbn);

      const info: BookInfo = {
        isbn,
        title: metadata?.title || `ISBN: ${isbn}`,
        author: metadata?.author || undefined,
        coverImageUrl: metadata?.coverImageUrl || undefined,
        publisher: metadata?.publisher || undefined,
        binding: metadata?.binding || undefined,
      };
      setBookInfo(info);

      if (!profile?.id) return;

      const dup = await checkForDuplicate(isbn);

      if (dup.exists) {
        setStep('found-adding');
        await addBookToLibrary(profile.id, dup.bookId);
        await handleBookAdded(dup.bookId);
      } else {
        setStep('not-found-requesting');
        await createBookScanRequest(profile.id, info.title, {
          isbn: info.isbn,
          author: info.author,
          coverImageUrl: info.coverImageUrl,
          publisher: info.publisher,
          binding: info.binding,
        });
        setStep('request-submitted');
      }
    } catch (err) {
      console.error('[AddBookFlow] Error processing manual entry:', err);
      Sentry.captureException(err, { tags: { flow: 'add_book', step: 'manual_submit' }, extra: { isbn: manualIsbn.trim() } });
      setError(t('Something went wrong. Please try again.'));
      setStep('choose-method');
    } finally {
      setIsProcessing(false);
    }
  }, [manualIsbn, profile?.id, handleBookAdded, t]);

  // ============================================================================
  // Turn Knowledge Handlers
  // ============================================================================

  const handleKnowledgeClassify = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!knowledgeTopic.trim()) return;

    setStep('knowledge-confirming');
    setKnowledgeError(null);

    try {
      const selectedChild = contextChildren.find(c => c.id === selectedChildId);
      const childName = selectedChild?.name || 'your child';
      const readingLevel = selectedChild?.in_color_program === false ? 'fluent' : 'emerging';
      const { data, error: fnError } = await supabase.functions.invoke('generate-knowledge-article', {
        body: { action: 'classify', topic: knowledgeTopic.trim(), childName, readingLevel },
      });

      if (fnError) throw fnError;

      if (data?.allowed) {
        setKnowledgeConfirmation(data.confirmation_message);
        setKnowledgeQuestionType(data.question_type || 'What');
        setKnowledgeTopicClean(data.topic_clean || knowledgeTopic.trim());
        setStep('knowledge-confirmed');
      } else {
        setKnowledgeError(data?.reason || t("That topic isn't available for articles right now. Try asking about something in the real world!"));
        setStep('knowledge-input');
      }
    } catch (err: any) {
      console.error('[AddBookFlow] Knowledge classify error:', err);
      Sentry.captureException(err, { tags: { flow: 'add_book', step: 'knowledge_classify' } });
      const serverMsg = err?.context?.body ? 'AI service is temporarily unavailable. Please try again later.' : null;
      setKnowledgeError(serverMsg || t('Something went wrong. Please try again.'));
      setStep('knowledge-input');
    }
  }, [knowledgeTopic, contextChildren, selectedChildId, t]);

  const handleKnowledgeConfirm = useCallback(async () => {
    if (!profile?.id || !selectedChildId) return;
    setIsProcessing(true);

    try {
      const selectedChild = contextChildren.find(c => c.id === selectedChildId);
      const readingLevel = selectedChild?.in_color_program === false ? 'fluent' : 'emerging';

      const { data: request, error: insertError } = await supabase
        .from('knowledge_article_requests')
        .insert({
          requested_by: profile.id,
          child_id: selectedChildId,
          topic: knowledgeTopicClean || knowledgeTopic.trim(),
          question_type: knowledgeQuestionType,
          reading_level: readingLevel,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // Trigger generation (fire-and-forget; admin reviews later)
      supabase.functions.invoke('generate-knowledge-article', {
        body: { action: 'generate', requestId: request.id },
      }).catch(err => {
        console.error('[AddBookFlow] Background generation error:', err);
        Sentry.captureException(err, { tags: { flow: 'add_book', step: 'knowledge_generate' }, extra: { requestId: request.id } });
      });

      setStep('knowledge-submitted');
    } catch (err) {
      console.error('[AddBookFlow] Knowledge confirm error:', err);
      Sentry.captureException(err, { tags: { flow: 'add_book', step: 'knowledge_confirm' } });
      setKnowledgeError(t('Something went wrong. Please try again.'));
      setStep('knowledge-input');
    } finally {
      setIsProcessing(false);
    }
  }, [profile?.id, selectedChildId, knowledgeTopicClean, knowledgeTopic, knowledgeQuestionType, t]);

  // ============================================================================
  // Render
  // ============================================================================

  const renderStep = () => {
    switch (step) {
      case 'choose-method':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-[#1C1C1E] text-center mb-6">
              {t('Request a Book')}
            </h2>
            
            {error && (
              <div className="p-3 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/20 text-[#FF3B30] text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              onClick={() => { setStep('choose-pages-method'); setError(null); }}
              className="w-full p-4 rounded-2xl flex items-center gap-4 transition-all hover:brightness-110"
              style={glassStyles.card}
            >
              <div className="w-12 h-12 bg-[#5C2D91]/15 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#5C2D91]" />
              </div>
              <div className="text-left">
                <div className="font-medium text-[#1C1C1E]">{t('Request Turn Pages')}</div>
                <div className="text-sm text-[#8E8E93]">{t('Add a book from your shelf')}</div>
              </div>
            </button>

            <button
              onClick={() => { setStep('knowledge-input'); setKnowledgeError(null); }}
              className="w-full p-4 rounded-2xl flex items-center gap-4 transition-all hover:brightness-110"
              style={glassStyles.card}
            >
              <div className="w-12 h-12 bg-[#0A84FF]/15 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-[#0A84FF]" />
              </div>
              <div className="text-left">
                <div className="font-medium text-[#1C1C1E]">{t('Request Turn Knowledge')}</div>
                <div className="text-sm text-[#8E8E93]">{t('Get a custom article about any topic')}</div>
              </div>
            </button>
          </div>
        );

      case 'choose-pages-method':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => setStep('choose-method')}
                className="p-2 rounded-full hover:bg-black/[0.05] transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#636366]" />
              </button>
              <h2 className="text-xl font-semibold text-[#1C1C1E]">
                {t('Request Turn Pages')}
              </h2>
            </div>

            <button
              onClick={startIsbnScanner}
              className="w-full p-4 rounded-2xl flex items-center gap-4 transition-all hover:brightness-110"
              style={glassStyles.card}
            >
              <div className="w-12 h-12 bg-[#5C2D91]/15 rounded-xl flex items-center justify-center">
                <Camera className="w-6 h-6 text-[#5C2D91]" />
              </div>
              <div className="text-left">
                <div className="font-medium text-[#1C1C1E]">{t('Scan ISBN Barcode')}</div>
                <div className="text-sm text-[#8E8E93]">{t("Use your camera to scan the book's barcode")}</div>
              </div>
            </button>

            <button
              onClick={() => { setStep('manual-entry'); setError(null); }}
              className="w-full p-4 rounded-2xl flex items-center gap-4 transition-all hover:brightness-110"
              style={glassStyles.card}
            >
              <div className="w-12 h-12 bg-[#34C759]/15 rounded-xl flex items-center justify-center">
                <Keyboard className="w-6 h-6 text-[#34C759]" />
              </div>
              <div className="text-left">
                <div className="font-medium text-[#1C1C1E]">{t('Enter ISBN')}</div>
                <div className="text-sm text-[#8E8E93]">{t("Type the book's ISBN number manually")}</div>
              </div>
            </button>
          </div>
        );

      case 'isbn-scan':
        return (
          <IsbnScanner
            onIsbnDetected={handleIsbnDetected}
            onClose={() => setStep('choose-pages-method')}
            onManualEntry={() => setStep('manual-entry')}
          />
        );

      case 'manual-entry':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => setStep('choose-pages-method')}
                className="p-2 rounded-full hover:bg-black/[0.05] transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#636366]" />
              </button>
              <h2 className="text-xl font-semibold text-[#1C1C1E]">{t('Enter ISBN')}</h2>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/20 text-[#FF3B30] text-sm">
                {error}
              </div>
            )}

            <p className="text-sm text-[#8E8E93]">
              {t("Find the ISBN barcode on the back of the book. It's usually a 10 or 13-digit number.")}
            </p>

            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#636366] mb-2">
                  {t('ISBN')} *
                </label>
                <input
                  type="text"
                  value={manualIsbn}
                  onChange={(e) => setManualIsbn(e.target.value)}
                  placeholder="e.g., 9780399226908"
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#AEAEB2] focus:outline-none focus:border-[#5C2D91]/50 text-lg tracking-wide"
                  autoFocus
                  inputMode="numeric"
                />
              </div>

              <button
                type="submit"
                disabled={!manualIsbn.trim()}
                className="w-full py-3 rounded-xl bg-[#5C2D91] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:brightness-110"
              >
                <Search className="w-4 h-4 inline mr-2" />
                {t('Look Up Book')}
              </button>
            </form>
          </div>
        );

      case 'checking':
        return (
          <div className="py-12 text-center space-y-4">
            <Loader2 className="w-12 h-12 text-[#5C2D91] animate-spin mx-auto" />
            <div className="text-[#1C1C1E] font-medium">{t('Searching our library...')}</div>
            {bookInfo && (
              <div className="text-sm text-[#8E8E93]">
                {t('Looking for')} "{bookInfo.title}"
              </div>
            )}
          </div>
        );

      case 'found-adding':
        return (
          <div className="py-12 text-center space-y-4">
            <Loader2 className="w-12 h-12 text-[#34C759] animate-spin mx-auto" />
            <div className="text-[#1C1C1E] font-medium">{t('Found it! Adding to your library...')}</div>
          </div>
        );

      case 'not-found-requesting':
        return (
          <div className="py-12 text-center space-y-4">
            <Loader2 className="w-12 h-12 text-[#FF9500] animate-spin mx-auto" />
            <div className="text-[#1C1C1E] font-medium">{t('Submitting request...')}</div>
          </div>
        );

      case 'choose-children':
        return (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <div className="w-12 h-12 bg-[#5C2D91]/15 rounded-full flex items-center justify-center mx-auto mb-3">
                <BookOpen className="w-6 h-6 text-[#5C2D91]" />
              </div>
              <h2 className="text-lg font-semibold text-[#1C1C1E]">{t('Who is this book for?')}</h2>
              {bookInfo && (
                <p className="text-sm text-[#8E8E93] mt-1">"{bookInfo.title}"</p>
              )}
            </div>

            <div className="space-y-2">
              {childOptions.map((child) => (
                <button
                  key={child.id}
                  onClick={() => setChildOptions(prev =>
                    prev.map(c => c.id === child.id ? { ...c, selected: !c.selected } : c)
                  )}
                  className={`
                    w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all
                    ${child.selected
                      ? 'bg-[#5C2D91]/10 border border-[#5C2D91]/30'
                      : 'bg-black/[0.03] border border-black/[0.08] hover:bg-black/[0.04]'
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${child.selected ? 'bg-[#5C2D91]/20 text-[#5C2D91]' : 'bg-black/[0.05] text-[#636366]'}
                    `}>
                      {child.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-[#1C1C1E]">{child.name}</span>
                  </div>
                  {child.selected && (
                    <Check className="w-5 h-5 text-[#5C2D91]" />
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={confirmChildAssignment}
              disabled={isProcessing || childOptions.every(c => !c.selected)}
              className="w-full py-3 rounded-xl bg-[#5C2D91] text-white font-medium disabled:opacity-50 transition-all hover:brightness-110 flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {childOptions.filter(c => c.selected).length === childOptions.length
                    ? t('Add for Everyone')
                    : `${t('Add for')} ${childOptions.filter(c => c.selected).map(c => c.name).join(', ')}`
                  }
                </>
              )}
            </button>
          </div>
        );

      case 'success':
        return (
          <div className="py-12 text-center space-y-6">
            <div className="w-16 h-16 bg-[#34C759]/15 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-[#34C759]" />
            </div>
            <div>
              <div className="text-xl font-semibold text-[#1C1C1E] mb-2">{t('Book Added!')}</div>
              {bookInfo && (
                <div className="text-[#8E8E93]">
                  "{bookInfo.title}" {t('is now in your library')}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-xl bg-[#34C759] text-white font-medium transition-all hover:brightness-110"
            >
              {t('Done')}
            </button>
          </div>
        );

      case 'request-submitted': {
        const readyDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
        const readyDateStr = readyDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

        return (
          <div className="py-6 text-center space-y-5">
            {/* Book preview card */}
            <div className="flex flex-col items-center gap-4">
              {bookInfo?.coverImageUrl ? (
                <img
                  src={bookInfo.coverImageUrl}
                  alt={bookInfo.title}
                  className="w-24 h-32 object-cover rounded-xl shadow-lg"
                />
              ) : (
                <div className="w-24 h-32 bg-black/[0.03] rounded-xl flex items-center justify-center border border-black/[0.08]">
                  <BookOpen className="w-10 h-10 text-[#AEAEB2]" />
                </div>
              )}

              {/* Requested badge */}
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF9500]/15 border border-[#FF9500]/30 text-[#FF9500] text-xs font-medium">
                <Clock className="w-3.5 h-3.5" />
                {t('Requested')}
              </span>
            </div>

            <div>
              <div className="text-lg font-semibold text-[#1C1C1E] mb-1">
                {bookInfo?.title || t('Book Requested')}
              </div>
              {bookInfo?.author && (
                <div className="text-sm text-[#8E8E93] mb-1">{t('by')} {bookInfo.author}</div>
              )}
              <div className="text-sm text-[#34C759] font-medium">
                {t('Ready by')} {readyDateStr}
              </div>
            </div>

            <div className="p-4 rounded-xl bg-black/[0.03] text-left">
              <p className="text-[#636366] text-sm leading-relaxed">
                {t("This book isn't in our library yet. Our team will scan and process it within")} <span className="text-[#1C1C1E] font-medium">{t('24 hours')}</span>. 
                {t("We'll notify you when it's ready!")}
              </p>
            </div>

            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl bg-[#5C2D91] text-white font-medium transition-all hover:brightness-110"
            >
              {t('Got it')}
            </button>
          </div>
        );
      }

      case 'knowledge-input': {
        const childName = contextChildren.find(c => c.id === selectedChildId)?.name;
        return (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setStep('choose-method'); setKnowledgeError(null); }}
                className="p-2 -ml-2 rounded-full hover:bg-black/[0.05] transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#636366]" />
              </button>
              <div>
                <h2 className="text-xl font-semibold text-[#1C1C1E]">{t('Turn Knowledge')}</h2>
                <p className="text-xs text-[#8E8E93] mt-0.5">{t('A custom article about any real-world topic')}</p>
              </div>
            </div>

            {knowledgeError && (
              <div className="p-3 rounded-xl bg-[#FF3B30]/8 border border-[#FF3B30]/20 text-[#FF3B30] text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{knowledgeError}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#1C1C1E] mb-2">
                {childName
                  ? t(`What would you like ${childName} to learn about?`)
                  : t('What would your child like to learn about?')}
              </label>

              <form onSubmit={handleKnowledgeClassify} className="space-y-4">
                <textarea
                  value={knowledgeTopic}
                  onChange={(e) => setKnowledgeTopic(e.target.value)}
                  placeholder={t('e.g., Why do volcanoes erupt? How do butterflies grow? What makes rainbows?')}
                  className="w-full px-4 py-3 rounded-xl bg-white border-2 border-[#E5E5EA] text-[#1C1C1E] placeholder-[#AEAEB2] focus:outline-none focus:border-[#0A84FF] text-base resize-none shadow-sm transition-colors"
                  rows={3}
                  autoFocus
                  maxLength={500}
                />
                <p className="text-xs text-[#AEAEB2] -mt-2">{t('Ask a question about animals, nature, science, history, or how things work')}</p>

                <button
                  type="submit"
                  disabled={!knowledgeTopic.trim()}
                  className="w-full py-3.5 rounded-xl bg-[#5C2D91] text-white font-semibold text-base disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:brightness-110 flex items-center justify-center gap-2 shadow-sm"
                >
                  <Send className="w-4 h-4" />
                  {t('Submit')}
                </button>
              </form>
            </div>
          </div>
        );
      }

      case 'knowledge-confirming':
        return (
          <div className="py-12 text-center space-y-4">
            <Loader2 className="w-12 h-12 text-[#0A84FF] animate-spin mx-auto" />
            <div className="text-[#1C1C1E] font-medium">{t('Checking your topic...')}</div>
          </div>
        );

      case 'knowledge-confirmed':
        return (
          <div className="space-y-5">
            <div className="text-center">
              <div className="w-14 h-14 bg-[#5C2D91]/12 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lightbulb className="w-7 h-7 text-[#5C2D91]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1C1C1E]">{t('Sound good?')}</h3>
            </div>

            <div className="p-4 rounded-xl bg-[#5C2D91]/5 border border-[#5C2D91]/15">
              <p className="text-[#1C1C1E] text-[15px] leading-relaxed">
                {knowledgeConfirmation}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setStep('knowledge-input'); setKnowledgeError(null); }}
                className="flex-1 py-3 rounded-xl border-2 border-[#E5E5EA] text-[#636366] font-medium transition-all hover:bg-black/[0.03]"
              >
                {t('Change topic')}
              </button>
              <button
                onClick={handleKnowledgeConfirm}
                disabled={isProcessing}
                className="flex-1 py-3 rounded-xl bg-[#5C2D91] text-white font-semibold disabled:opacity-50 transition-all hover:brightness-110 flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>{t('Go ahead!')}</>
                )}
              </button>
            </div>
          </div>
        );

      case 'knowledge-submitted':
        return (
          <div className="py-8 text-center space-y-5">
            <div className="w-16 h-16 bg-[#34C759]/12 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-[#34C759]" />
            </div>
            <div>
              <div className="text-xl font-bold text-[#1C1C1E] mb-1">{t('Article Requested!')}</div>
              <div className="text-[15px] text-[#636366]">
                {t('It will be ready within 24 hours.')}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-[#34C759]/5 border border-[#34C759]/15 text-left">
              <p className="text-[#1C1C1E] text-sm leading-relaxed">
                {t("We'll notify you when the article is ready to read. It will appear in your library just like a regular book.")}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full py-3.5 rounded-xl bg-[#5C2D91] text-white font-semibold text-base transition-all hover:brightness-110 shadow-sm"
            >
              {t('Done')}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div 
        className="w-full max-w-md rounded-3xl p-6 relative"
        style={glassStyles.modal}
      >
        {/* Close button (only on certain steps) */}
        {['choose-method', 'choose-pages-method', 'manual-entry', 'choose-children', 'knowledge-input', 'knowledge-confirmed'].includes(step) && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/[0.05] transition-colors"
          >
            <X className="w-5 h-5 text-[#636366]" />
          </button>
        )}

        {renderStep()}
      </div>
    </div>
  );
}

export default AddBookFlow;
